import React from 'react';
import { Delete, DragHandle, Edit } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { useLocales } from '../../../../../hooks';
import { HeroModuleItemType } from '../../../../../API';
import IconButton from '../../../../shared/IconButton';
import { Chip, Typography } from '@mui/material';
import { IdBadge } from '../../../../shared/IdBadge';
import { contentItemPreviewTestIds } from '../../../../shared/TestsIds';

export type ContentItem = {
  id: string;
  contentId: string;
  type?: HeroModuleItemType.VOD;
};

export type ContentItemPreviewProps = {
  contentItem: ContentItem;
  index: number;
  onRemove: (index: number) => unknown;
  onEdit: () => unknown;
};

const useStyles = makeStyles()((theme) => ({
  rootContainer: {
    display: 'flex',
    gap: theme.spacing(4),
    alignItems: 'center'
  },
  dragHandle: {
    cursor: 'grab'
  },
  contentItemInfo: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    overflow: 'hidden'
  },
  option: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center'
  },
  controlButtons: {
    whiteSpace: 'nowrap'
  }
}));

export function ContentItemPreview({
  contentItem,
  index,
  onRemove,
  onEdit
}: ContentItemPreviewProps): React.ReactElement {
  const { classes } = useStyles();
  const { t } = useLocales();

  const handleRemove = () => {
    onRemove(index);
  };

  const getTypeLabel = (type?: HeroModuleItemType.VOD) => {
    switch (type) {
      case HeroModuleItemType.VOD:
        return t('general.vod');
      default:
        return t('general.content');
    }
  };

  return (
    <div className={classes.rootContainer} data-testid={contentItemPreviewTestIds.root}>
      <DragHandle className={classes.dragHandle} />
      <div className={classes.contentItemInfo}>
        {contentItem.type && (
          <div className={classes.option}>
            <Typography variant="body2" color="textSecondary">
              {t('layouts.type')}:
            </Typography>
            <Chip label={getTypeLabel(contentItem.type)} size="small" />
          </div>
        )}

        <div className={classes.option}>
          <Typography variant="body2" color="textSecondary">
            {t('layouts.hero_content_label')}:
          </Typography>
          <IdBadge id={contentItem.contentId} />
        </div>
      </div>
      <div className={classes.controlButtons}>
        <IconButton
          onClick={onEdit}
          title={t('layouts.hero_edit_content_item')}
          size="large"
          data-testid={contentItemPreviewTestIds.editItemButton}
        >
          <Edit />
        </IconButton>
        <IconButton
          onClick={handleRemove}
          title={t('layouts.hero_delete_content_item')}
          size="large"
          data-testid={contentItemPreviewTestIds.deleteItemButton}
        >
          <Delete />
        </IconButton>
      </div>
    </div>
  );
}
